import { Reducer } from '@reduxjs/toolkit';
import { OrganizationTabsActions } from 'store/actions';
import { IOrganizationTabsState } from '../../types/interfaces';
import { IOrganizationTabActionTypes } from '../../types/interfaces';
import {
    ORGANIZATION_VOTER_INTERESTS_FIELD_BY_TYPE,
    ORGANIZATION_VOTER_INTERESTS_FIELDS,
    ORGANIZATION_VOTER_INTERESTS_TYPES,
} from '../../constants/organizationTabs';

const defaultState: IOrganizationTabsState = {
    error: undefined,
    organizationTabs: {
        data: [],
        loading: false,
        lastUpdate: null,
    },
    organizationActiveDelegates: {
        data: [],
        loading: false,
        lastUpdate: null,
    },
    organizationTopDelegates: {
        data: [],
        loading: false,
        lastUpdate: null,
        delegatesType: null,
    },
    organizationActiveVoters: {
        data: [],
        loading: false,
        lastUpdate: null,
    },
    organizationTopVoters: {
        data: [],
        loading: false,
        lastUpdate: null,
    },
    organizationProposalsTrends: {
        data: [],
        loading: false,
        lastUpdate: null,
    },
    organizationSuccessfulProposalsTrends: {
        data: [],
        loading: false,
        lastUpdate: null,
    },
    organizationVotesTrends: {
        data: [],
        loading: false,
        lastUpdate: null,
    },
    organizationVotersTrends: {
        data: [],
        loading: false,
        lastUpdate: null,
    },
    organizationAverageVotesPerProposalTrends: {
        data: [],
        loading: false,
        lastUpdate: null,
    },
    [ORGANIZATION_VOTER_INTERESTS_FIELDS.YEAR_BY_YEAR]: {
        data: [],
        lastUpdateDate: null,
        loading: false,
        error: null,
    },
    [ORGANIZATION_VOTER_INTERESTS_FIELDS.VOTER_RANKS]: {
        data: [],
        lastUpdateDate: null,
        loading: false,
        error: null,
    },
    [ORGANIZATION_VOTER_INTERESTS_FIELDS.VOTER_ECOSYSTEM_PARTICIPATION]: {
        data: [],
        lastUpdateDate: null,
        loading: false,
        error: null,
    },
    [ORGANIZATION_VOTER_INTERESTS_FIELDS.BREAKDOWN_ECOSYSTEM]: {
        data: [],
        lastUpdateDate: null,
        loading: false,
        error: null,
    },
    [ORGANIZATION_VOTER_INTERESTS_FIELDS.BREAKDOWN_ORG]: {
        data: [],
        lastUpdateDate: null,
        loading: false,
        error: null,
    },
    [ORGANIZATION_VOTER_INTERESTS_FIELDS.COMPETITOR_DAOS_FOR_ORG_VOTERS]: {
        data: [],
        lastUpdateDate: null,
        loading: false,
        error: null,
    },
    [ORGANIZATION_VOTER_INTERESTS_FIELDS.COMPETITOR_DAOS_FOR_ORG_TOKEN_HOLDERS]: {
        data: [],
        lastUpdateDate: null,
        loading: false,
        error: null,
    },
    organizationTreasuryTrends: {
        data: [],
        loading: false,
        lastUpdate: null,
    },
    organizationTreasuryCompositionGraph: {
        data: null,
        loading: false,
    },
    organizationVotingToken: {
        data: null,
        loading: false,
        lastUpdate: null,
    },
    organizationAvgVotesAndTokenPriceGraph: {
        data: [],
        loading: false,
        lastUpdate: null,
    },
    organizationTreasuryComposition: {
        data: [],
        loading: false,
    },
    organizationHoldingToken: {
        data: [],
        loading: false,
    },
    organizationVoterGroups: {
        data: null,
        loading: false,
    },
};

export const organizationTabsReducer: Reducer<IOrganizationTabsState, IOrganizationTabActionTypes> = (
    state: IOrganizationTabsState = defaultState,
    action: any,
): IOrganizationTabsState => {
    const { type, payload } = action;

    switch (type) {
        // general
        case OrganizationTabsActions.GET_ORGANIZATION_TABS_SUCCESS: {
            const { organizationTabs } = payload as any;

            return {
                ...state,
                organizationTabs: {
                    data: organizationTabs,
                    loading: false,
                    lastUpdate: null,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_TABS_FAILURE: {
            return {
                ...state,
                organizationTabs: {
                    data: [],
                    loading: false,
                    lastUpdate: null,
                },
            };
        }

        // delegation insights tab
        case OrganizationTabsActions.GET_ORGANIZATION_ACTIVE_DELEGATES: {
            return {
                ...state,
                organizationActiveDelegates: {
                    data: [],
                    loading: true,
                    lastUpdate: null,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_ACTIVE_DELEGATES_SUCCESS: {
            const { organizationActiveDelegates, lastUpdate } = payload as any;

            return {
                ...state,
                organizationActiveDelegates: {
                    data: organizationActiveDelegates,
                    loading: false,
                    lastUpdate,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_ACTIVE_DELEGATES_FAILURE: {
            return {
                ...state,
                error: (action as any)?.error,
                organizationActiveDelegates: {
                    data: [],
                    loading: false,
                    lastUpdate: null,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_TOP_DELEGATES: {
            return {
                ...state,
                organizationTopDelegates: {
                    data: [],
                    loading: true,
                    lastUpdate: null,
                    delegatesType: null,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_TOP_DELEGATES_SUCCESS: {
            const { organizationTopDelegates, lastUpdate, delegatesType } = payload as any;

            return {
                ...state,
                organizationTopDelegates: {
                    data: organizationTopDelegates,
                    loading: false,
                    lastUpdate,
                    delegatesType,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_TOP_DELEGATES_FAILURE: {
            return {
                ...state,
                error: (action as any)?.error,
                organizationTopDelegates: {
                    data: [],
                    loading: false,
                    lastUpdate: null,
                    delegatesType: null,
                },
            };
        }

        // voter decentralization tab
        case OrganizationTabsActions.GET_ORGANIZATION_ACTIVE_VOTERS: {
            return {
                ...state,
                organizationActiveVoters: {
                    data: [],
                    loading: true,
                    lastUpdate: null,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_ACTIVE_VOTERS_SUCCESS: {
            const { organizationActiveVoters, lastUpdate } = payload as any;

            return {
                ...state,
                organizationActiveVoters: {
                    data: organizationActiveVoters,
                    loading: false,
                    lastUpdate,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_ACTIVE_VOTERS_FAILURE: {
            return {
                ...state,
                error: (action as any)?.error,
                organizationActiveVoters: {
                    data: [],
                    loading: false,
                    lastUpdate: null,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_TOP_VOTERS: {
            return {
                ...state,
                organizationTopVoters: {
                    data: [],
                    loading: true,
                    lastUpdate: null,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_TOP_VOTERS_SUCCESS: {
            const { organizationTopVoters, lastUpdate } = payload as any;

            return {
                ...state,
                organizationTopVoters: {
                    data: organizationTopVoters,
                    loading: false,
                    lastUpdate,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_TOP_VOTERS_FAILURE: {
            return {
                ...state,
                error: (action as any)?.error,
                organizationTopVoters: {
                    data: [],
                    loading: false,
                    lastUpdate: null,
                },
            };
        }

        // voters insights tab
        case OrganizationTabsActions.GET_ORGANIZATION_PROPOSALS_TRENDS: {
            return {
                ...state,
                organizationProposalsTrends: {
                    data: [],
                    loading: true,
                    lastUpdate: null,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_PROPOSALS_TRENDS_SUCCESS: {
            const { organizationProposalsTrends, lastUpdate } = payload as any;

            return {
                ...state,
                organizationProposalsTrends: {
                    data: organizationProposalsTrends,
                    loading: false,
                    lastUpdate,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_PROPOSALS_TRENDS_FAILURE: {
            return {
                ...state,
                error: (action as any)?.error,
                organizationProposalsTrends: {
                    data: [],
                    loading: false,
                    lastUpdate: null,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_SUCCESSFUL_PROPOSALS_TRENDS: {
            return {
                ...state,
                organizationSuccessfulProposalsTrends: {
                    data: [],
                    loading: true,
                    lastUpdate: null,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_SUCCESSFUL_PROPOSALS_TRENDS_SUCCESS: {
            const { organizationSuccessfulProposalsTrends, lastUpdate } = payload as any;

            return {
                ...state,
                organizationSuccessfulProposalsTrends: {
                    data: organizationSuccessfulProposalsTrends,
                    loading: false,
                    lastUpdate,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_SUCCESSFUL_PROPOSALS_TRENDS_FAILURE: {
            return {
                ...state,
                error: (action as any)?.error,
                organizationSuccessfulProposalsTrends: {
                    data: [],
                    loading: false,
                    lastUpdate: null,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_VOTES_TRENDS: {
            return {
                ...state,
                organizationVotesTrends: {
                    data: [],
                    loading: true,
                    lastUpdate: null,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_VOTES_TRENDS_SUCCESS: {
            const { organizationVotesTrends, lastUpdate } = payload as any;

            return {
                ...state,
                organizationVotesTrends: {
                    data: organizationVotesTrends,
                    loading: false,
                    lastUpdate,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_VOTES_TRENDS_FAILURE: {
            return {
                ...state,
                error: (action as any)?.error,
                organizationVotesTrends: {
                    data: [],
                    loading: false,
                    lastUpdate: null,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_VOTERS_TRENDS: {
            return {
                ...state,
                organizationVotersTrends: {
                    data: [],
                    loading: true,
                    lastUpdate: null,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_VOTERS_TRENDS_SUCCESS: {
            const { organizationVotersTrends, lastUpdate } = payload as any;

            return {
                ...state,
                organizationVotersTrends: {
                    data: organizationVotersTrends,
                    loading: false,
                    lastUpdate,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_VOTERS_TRENDS_FAILURE: {
            return {
                ...state,
                error: (action as any)?.error,
                organizationVotersTrends: {
                    data: [],
                    loading: false,
                    lastUpdate: null,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_AVERAGE_VOTES_PER_PROPOSAL_TRENDS: {
            return {
                ...state,
                organizationAverageVotesPerProposalTrends: {
                    data: [],
                    loading: true,
                    lastUpdate: null,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_AVERAGE_VOTES_PER_PROPOSAL_TRENDS_SUCCESS: {
            const { organizationAverageVotesPerProposalTrends, lastUpdate } = payload as any;

            return {
                ...state,
                organizationAverageVotesPerProposalTrends: {
                    data: organizationAverageVotesPerProposalTrends,
                    loading: false,
                    lastUpdate,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_AVERAGE_VOTES_PER_PROPOSAL_TRENDS_FAILURE: {
            return {
                ...state,
                error: (action as any)?.error,
                organizationAverageVotesPerProposalTrends: {
                    data: [],
                    loading: false,
                    lastUpdate: null,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_YEAR_BY_YEAR_VOTES: {
            return {
                ...state,
                oviYearByYear: {
                    data: [],
                    loading: true,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_YEAR_BY_YEAR_VOTES_SUCCESS: {
            const {
                organizationYearByYearVotes: { aggregatedData, createdAt },
            } = payload as any;

            return {
                ...state,
                oviYearByYear: {
                    data: aggregatedData,
                    lastUpdateDate: createdAt,
                    loading: false,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_YEAR_BY_YEAR_VOTES_FAILURE: {
            return {
                ...state,
                error: (action as any)?.error,
                oviYearByYear: {
                    data: [],
                    loading: false,
                },
            };
        }

        // voter interests tab
        case OrganizationTabsActions.GET_ORGANIZATION_VOTER_INTERESTS: {
            return {
                ...state,
                [ORGANIZATION_VOTER_INTERESTS_FIELD_BY_TYPE[payload.tableType as ORGANIZATION_VOTER_INTERESTS_TYPES]]: {
                    data: [],
                    loading: true,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_VOTER_INTERESTS_SUCCESS: {
            const {
                organizationVoterInterests: { voterInterestsType, aggregatedData, createdAt },
            } = payload;

            return {
                ...state,
                [ORGANIZATION_VOTER_INTERESTS_FIELD_BY_TYPE[voterInterestsType as ORGANIZATION_VOTER_INTERESTS_TYPES]]:
                    {
                        data: aggregatedData,
                        lastUpdateDate: createdAt,
                        loading: false,
                    },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_VOTER_INTERESTS_FAILURE: {
            return {
                ...state,
                error: (action as any)?.error,
                [ORGANIZATION_VOTER_INTERESTS_FIELD_BY_TYPE[payload.tableType as ORGANIZATION_VOTER_INTERESTS_TYPES]]: {
                    data: [],
                    loading: false,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_VOTER_GROUPS: {
            return {
                ...state,
                organizationVoterGroups: {
                    data: null,
                    loading: true,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_VOTER_GROUPS_SUCCESS: {
            const { organizationVoterGroups } = payload;

            return {
                ...state,
                organizationVoterGroups: {
                    data: organizationVoterGroups,
                    loading: false,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_VOTER_GROUPS_FAILURE: {
            return {
                ...state,
                error: (action as any)?.error,
                organizationVoterGroups: {
                    data: null,
                    loading: false,
                },
            };
        }

        // finance tab
        case OrganizationTabsActions.GET_ORGANIZATION_TREASURY_TRENDS: {
            return {
                ...state,
                organizationTreasuryTrends: {
                    data: [],
                    loading: true,
                    lastUpdate: null,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_TREASURY_TRENDS_SUCCESS: {
            const { organizationTreasuryTrends, lastUpdate } = payload as any;

            return {
                ...state,
                organizationTreasuryTrends: {
                    data: organizationTreasuryTrends,
                    loading: false,
                    lastUpdate: lastUpdate,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_TREASURY_TRENDS_FAILURE: {
            return {
                ...state,
                error: (action as any)?.error,
                organizationTreasuryTrends: {
                    data: [],
                    loading: false,
                    lastUpdate: null,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_TREASURY_COMPOSITION_GRAPH: {
            return {
                ...state,
                organizationTreasuryCompositionGraph: {
                    data: null,
                    loading: true,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_TREASURY_COMPOSITION_GRAPH_SUCCESS: {
            const { organizationTreasuryCompositionGraph } = payload as any;

            return {
                ...state,
                organizationTreasuryCompositionGraph: {
                    data: organizationTreasuryCompositionGraph,
                    loading: false,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_TREASURY_COMPOSITION_GRAPH_FAILURE: {
            return {
                ...state,
                error: (action as any)?.error,
                organizationTreasuryCompositionGraph: {
                    data: null,
                    loading: false,
                },
            };
        }

        // token tab
        case OrganizationTabsActions.GET_ORGANIZATION_VOTING_TOKEN: {
            return {
                ...state,
                organizationVotingToken: {
                    data: null,
                    loading: true,
                    lastUpdate: null,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_VOTING_TOKEN_SUCCESS: {
            const { organizationVotingToken, lastUpdate } = payload as any;

            return {
                ...state,
                organizationVotingToken: {
                    data: organizationVotingToken,
                    loading: false,
                    lastUpdate: lastUpdate,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_VOTING_TOKEN_FAILURE: {
            return {
                ...state,
                error: (action as any)?.error,
                organizationVotingToken: {
                    data: null,
                    loading: false,
                    lastUpdate: null,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_AVG_VOTES_AND_TOKEN_PRICE: {
            return {
                ...state,
                organizationAvgVotesAndTokenPriceGraph: {
                    data: [],
                    loading: true,
                    lastUpdate: null,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_AVG_VOTES_AND_TOKEN_PRICE_SUCCESS: {
            const { organizationAvgVotesAndTokenPriceGraph, lastUpdate } = payload as any;

            return {
                ...state,
                organizationAvgVotesAndTokenPriceGraph: {
                    data: organizationAvgVotesAndTokenPriceGraph,
                    loading: false,
                    lastUpdate: lastUpdate,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_AVG_VOTES_AND_TOKEN_PRICE_FAILURE: {
            return {
                ...state,
                error: (action as any)?.error,
                organizationAvgVotesAndTokenPriceGraph: {
                    data: [],
                    loading: false,
                    lastUpdate: null,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_TREASURY_COMPOSITION: {
            return {
                ...state,
                organizationTreasuryComposition: {
                    data: [],
                    loading: true,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_TREASURY_COMPOSITION_SUCCESS: {
            const { organizationTreasuryComposition } = payload as any;

            return {
                ...state,
                organizationTreasuryComposition: {
                    data: organizationTreasuryComposition,
                    loading: false,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_TREASURY_COMPOSITION_FAILURE: {
            return {
                ...state,
                error: (action as any)?.error,
                organizationTreasuryComposition: {
                    data: [],
                    loading: false,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_HOLDING_TOKEN: {
            return {
                ...state,
                organizationHoldingToken: {
                    data: [],
                    loading: true,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_HOLDING_TOKEN_SUCCESS: {
            const { organizationHoldingToken } = payload as any;

            return {
                ...state,
                organizationHoldingToken: {
                    data: organizationHoldingToken,
                    loading: false,
                },
            };
        }

        case OrganizationTabsActions.GET_ORGANIZATION_HOLDING_TOKEN_FAILURE: {
            return {
                ...state,
                error: (action as any)?.error,
                organizationHoldingToken: {
                    data: [],
                    loading: false,
                },
            };
        }

        default: {
            return state;
        }
    }
};
